import { supabase } from '../lib/supabaseClient';

/* 기획전 미러 (Supabase) — 대리점 포털(jn_crs) 노출용
 * ===========================================================================
 * 원본은 ERP(MSSQL)의 DBO.기획전 / DBO.상품가격_기획전 이고 여기는 복제본이다.
 * ERP 에 신규 SP 를 배포할 수 없어서, 대리점 포털이 "기획전에 담긴 개별 출발일"을
 * 알 수 있도록 trp 가 저장 시점에 같은 내용을 Supabase 로 dual-write 한다.
 *
 * 원칙
 * ---------------------------------------------------------------------------
 * 1) ERP 저장이 성공한 뒤에만 미러링한다. 원본이 진실이고 미러는 따라간다.
 * 2) 미러 실패가 ERP 저장을 되돌리지 않는다 — 이미 커밋된 원본을 되돌릴 방법이
 *    없고, 미러는 다음 저장 때 다시 맞춰진다. 그래서 호출부는 실패를 삼키고
 *    경고만 남긴다. (반환값으로 성공/실패를 알려주므로 UI 가 안내할 수 있다)
 * 3) Supabase 미설정(anon key 없음)이면 조용히 skip 한다 — 기존 estimate_extras
 *    와 같은 동작. trp 단독 운영 환경을 깨뜨리지 않는다.
 *
 * ⚠ 백필: write-time 미러라서 이 기능 도입 이전에 ERP 에서 매핑된 기획전은
 *   여기 없다. trp 에서 한 번씩 다시 저장해야 대리점 포털에 나타난다.
 */

/** 미러 결과. ok=false 여도 ERP 원본은 이미 저장된 상태다. */
const ok = () => ({ ok: true, skipped: false, error: null });
const skipped = () => ({ ok: true, skipped: true, error: null });
const failed = (error) => ({ ok: false, skipped: false, error });

const toDateOrNull = (v) => {
  const s = String(v ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
};

const toNumOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * 기획전 헤더 미러 (등록/수정 공통).
 * ERP 의 RCT_T_기획전 저장 성공 후 호출한다.
 */
export const mirrorPromotion = async ({
  promotionNo,
  companyNo,
  name,
  startDate,
  endDate,
  manager = '',
  description = '',
  terms = '',
  url = '',
  productKinds = '',
  closed = false,
} = {}) => {
  if (!supabase) return skipped();

  const 기획전번호 = toNumOrNull(promotionNo);
  const 공급업체번호 = toNumOrNull(companyNo);
  if (!기획전번호 || !공급업체번호) {
    return failed(new Error('기획전번호/공급업체번호가 없어 미러링할 수 없습니다.'));
  }

  const { error } = await supabase.from('promotions').upsert(
    {
      기획전번호,
      공급업체번호,
      기획전명: String(name ?? ''),
      시작일: toDateOrNull(startDate),
      종료일: toDateOrNull(endDate),
      담당자: String(manager ?? ''),
      기획전설명: String(description ?? ''),
      이용약관: String(terms ?? ''),
      기획전url: String(url ?? ''),
      상품종류: String(productKinds ?? ''),
      마감상태: !!closed,
      synced_at: new Date().toISOString(),
    },
    { onConflict: '기획전번호' },
  );

  return error ? failed(error) : ok();
};

/** 기획전 삭제 미러. promotion_departures 는 FK on delete cascade 로 함께 지워진다. */
export const mirrorPromotionDelete = async ({ promotionNo } = {}) => {
  if (!supabase) return skipped();

  const 기획전번호 = toNumOrNull(promotionNo);
  if (!기획전번호) return failed(new Error('기획전번호가 없어 미러링할 수 없습니다.'));

  const { error } = await supabase.from('promotions').delete().eq('기획전번호', 기획전번호);
  return error ? failed(error) : ok();
};

/**
 * 날짜(가격번호) 매핑 미러.
 *
 * ERP 의 RCT_T_상품가격_기획전연동은 "추가"가 아니라 "교체"다 — 선택 가격번호의
 * 기존 매핑을 기획전 조건 없이 전부 DELETE 한 뒤 (가격번호 × 기획전)으로 INSERT 한다.
 * 미러도 정확히 같은 시맨틱을 재현해야 원본과 어긋나지 않는다:
 *
 *   1) 이 가격번호들의 기존 매핑을 (기획전 무관) 전부 삭제
 *   2) 선택된 기획전 × 가격번호로 다시 삽입
 *
 * promotionNos 가 비면 1)만 수행되어 전체 해제가 된다 — ERP 와 동일.
 *
 * 소유 검증 (fail-closed)
 * ---------------------------------------------------------------------------
 * 매핑 모달의 기획전 목록은 로그인 업체가 등록한 기획전만 보여 주지만,
 * (과거 RCT_S_공용기획전 은 공급업체번호 IN (1,내업체) 로 본사 공용을 섞어 내렸고
 *  SELECT 에 공급업체번호가 없었다.)
 *
 * 그래서 미러 헤더(promotions)에 내 공급업체번호로 이미 등록된 기획전에만 출발일을
 * 붙인다. 헤더는 기획전 관리 화면의 저장 경로에서만 기록되고, 그 경로는 ERP
 * RCT_T_기획전 이 등록 공급업체를 강제하므로 소유가 증명된 것만 들어온다.
 * 증명되지 않은 기획전(본사 공용, 혹은 아직 재저장하지 않은 기존 기획전)은
 * skippedPromotionNos 로 돌려주어 호출부가 안내할 수 있게 한다.
 *
 * @param departures [{ 가격번호, 출발일 }] — 상품번호/상품명 등은 상품 단위로 공통
 */
export const mirrorPriceMapping = async ({
  promotionNos = [],
  departures = [],
  companyNo,
  productNo,
  productName = '',
  productKind = '',
  productKindCode = '',
} = {}) => {
  if (!supabase) return skipped();

  const 공급업체번호 = toNumOrNull(companyNo);
  const 상품번호 = toNumOrNull(productNo);
  if (!공급업체번호 || !상품번호) {
    return failed(new Error('공급업체번호/상품번호가 없어 미러링할 수 없습니다.'));
  }

  const rows = (Array.isArray(departures) ? departures : [])
    .map((d) => ({ 가격번호: toNumOrNull(d?.가격번호 ?? d?.priceNo), 출발일: toDateOrNull(d?.출발일 ?? d?.date) }))
    .filter((d) => d.가격번호 && d.가격번호 > 0);

  const priceNos = Array.from(new Set(rows.map((r) => r.가격번호)));
  if (priceNos.length === 0) return failed(new Error('미러링할 가격번호가 없습니다.'));

  // 1) 교체 시맨틱 — 이 가격번호들의 기존 매핑을 기획전 조건 없이 전부 제거
  const { error: deleteError } = await supabase
    .from('promotion_departures')
    .delete()
    .in('가격번호', priceNos);
  if (deleteError) return failed(deleteError);

  const promoNos = (Array.isArray(promotionNos) ? promotionNos : [])
    .map(toNumOrNull)
    .filter((n) => n && n > 0);
  if (promoNos.length === 0) return ok(); // 전체 해제

  // 2) 소유가 증명된 기획전만 골라낸다 (위 주석의 fail-closed 규칙)
  const { data: ownedRows, error: ownedError } = await supabase
    .from('promotions')
    .select('기획전번호')
    .eq('공급업체번호', 공급업체번호)
    .in('기획전번호', promoNos);
  if (ownedError) return failed(ownedError);

  const owned = new Set((ownedRows || []).map((r) => Number(r.기획전번호)));
  const skippedPromotionNos = promoNos.filter((n) => !owned.has(n));
  const targets = promoNos.filter((n) => owned.has(n));

  if (targets.length === 0) return { ...ok(), skippedPromotionNos };

  // 3) (기획전 × 가격번호) 삽입
  const synced_at = new Date().toISOString();
  const payload = targets.flatMap((기획전번호) =>
    rows.map((r) => ({
      기획전번호,
      가격번호: r.가격번호,
      공급업체번호,
      상품번호,
      출발일: r.출발일,
      상품명: String(productName ?? ''),
      상품종류: String(productKind ?? ''),
      상품종류코드: String(productKindCode ?? ''),
      synced_at,
    })),
  );

  const { error: insertError } = await supabase
    .from('promotion_departures')
    .upsert(payload, { onConflict: '기획전번호,가격번호' });

  return insertError ? failed(insertError) : { ...ok(), skippedPromotionNos };
};

export default { mirrorPromotion, mirrorPromotionDelete, mirrorPriceMapping };
